import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { BLOG_POSTS } from "@/data/blog";
import { useGetUpcomingEvents, useGetReviews } from "@workspace/api-client-react";
import { useSeo } from "@/hooks/useSeo";
import { openBookingModal } from "@/components/booking-modal";
import { useWeather, WeatherStrip } from "@/components/weather-widget";
import { useCountUp } from "@/hooks/useCountUp";
import { REVIEW_STATS, formatReviewCount } from "@/data/review-stats";
import { format, parseISO } from "date-fns";
import { ru } from "date-fns/locale";

import MapPicker from "@/components/map-picker";
import { NewYearAnnouncement } from "@/components/new-year/new-year-announcement";

function getSpbNow() {
  const now = new Date();
  return new Date(now.toLocaleString("en-US", { timeZone: "Europe/Moscow" }));
}

function getTodayHours(): string {
  const spb = getSpbNow();
  const day = spb.getDay();
  return (day === 5 || day === 6) ? "12:00 — 01:00" : "12:00 — 23:00";
}

function isOpenNow(): boolean {
  const spb = getSpbNow();
  const h = spb.getHours();
  const day = spb.getDay();
  // 00:00–01:00: open only if previous day was Fri or Sat
  if (h === 0) {
    const prevDay = day === 0 ? 6 : day - 1;
    return prevDay === 5 || prevDay === 6;
  }
  if (h < 12) return false;
  if (day === 5 || day === 6) return true; // Fri–Sat close at 01:00 (next day)
  return h < 23;
}

const tickerPhotos = [
  { src: "/photos/view_sunset-sm.webp", alt: "Закат над крышами Петербурга — вид с террасы ресторана Паруса" },
  { src: "/photos/view_day-sm.webp", alt: "Дневная панорама Санкт-Петербурга с крыши ресторана" },
  { src: "/photos/interior-sm.webp", alt: "Интерьер ресторана Паруса на крыше — зал с панорамными окнами" },
  { src: "/photos/view_night-sm.webp", alt: "Ночной Петербург с крыши ресторана Паруса на крыше" },
  { src: "/photos/view_summer-sm.webp", alt: "Летняя открытая терраса ресторана на крыше в Петербурге" },
  { src: "/photos/view_clouds-sm.webp", alt: "Облачное небо над Петербургом — вид с видовой террасы ресторана" },
  { src: "/photos/food-sm.webp", alt: "Авторская кухня ресторана Паруса на крыше" },
  { src: "/photos/terrace-sm.webp", alt: "Открытая терраса ресторана Паруса с видом на город" },
];

function UspCounter({ value, label, sub }: { value: string; label: string; sub: string }) {
  const { display, ref } = useCountUp(value);
  return (
    <div ref={ref} className="px-8 py-10 text-center lg:text-left">
      <div className="font-serif text-5xl font-light text-primary mb-2">{display}</div>
      <div className="text-foreground text-sm font-medium mb-1">{label}</div>
      <div className="text-muted-foreground text-xs">{sub}</div>
    </div>
  );
}

export default function Home() {
  const [, setLocation] = useLocation();
  const weather = useWeather();
  const [open] = useState(isOpenNow);
  const [todayHours] = useState(getTodayHours);

  useSeo({
    title: "Паруса на крыше — Ресторан на крыше в Санкт-Петербурге",
    description: `★★★★★ 5,0 · ${formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов на Яндексе. Ресторан на крыше в центре Петербурга — панорама 300° на город, закаты над Невой, живая музыка. Ул. Марата, 5, 6 этаж. С 2009 года.`,
    keywords: "ресторан на крыше Петербург, Паруса на крыше, панорамный ресторан СПб, ресторан ул Марата 5, ресторан Olympic Plaza Петербург, вид на город ресторан, закат Петербург ресторан",
    image: "/photos/view_sunset.jpg",
    canonical: "/",
  });

  const { data: upcomingEvents } = useGetUpcomingEvents();
  const { data: reviews } = useGetReviews();

  // Lazy-load the hero video AFTER first paint so the poster image is the LCP
  // and the 8MB→1.5MB clip never blocks initial render on mobile.
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  useEffect(() => {
    const start = () => setShowHeroVideo(true);
    const ric = (window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) {
      const id = ric(start, { timeout: 3000 });
      return () => (window as typeof window & {
        cancelIdleCallback?: (id: number) => void;
      }).cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(start, 2500);
    return () => clearTimeout(id);
  }, []);

  const formatDate = (dateString: string) => {
    try { return format(parseISO(dateString), "d MMMM", { locale: ru }); }
    catch { return dateString; }
  };

  return (
    <div className="w-full">

      {/* ── HERO: full-bleed image (LCP) with lazy video overlay ─────────── */}
      <section className="relative h-[100dvh] w-full overflow-hidden bg-background">
        {/* Poster image — the LCP element, preloaded with high priority */}
        <img
          src="/photos/view_sunset.webp"
          alt=""
          aria-hidden="true"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {showHeroVideo && (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster="/photos/view_sunset.webp"
            className="absolute inset-0 w-full h-full object-cover object-center"
          >
            <source src="/hero3.mp4" type="video/mp4" />
            <track kind="captions" srcLang="ru" label="Без звука" />
          </video>
        )}
        {/* Left-heavy dark gradient so text is readable, right side shows the view */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(3,13,9,0.90) 0%, rgba(3,13,9,0.65) 45%, rgba(3,13,9,0.20) 100%)",
          }}
        />
        {/* Bottom fade for info band */}
        <div
          className="absolute inset-x-0 bottom-0 h-48 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(3,13,9,0.95) 0%, transparent 100%)" }}
        />

        {/* Content — pb on mobile keeps content above the bottom strip (~130px tall on mobile) */}
        <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 max-w-3xl pb-[130px] md:pb-0">
          <div className="flex flex-col gap-3 mb-6 md:mb-8 self-start">
            <div className="inline-flex items-center gap-3">
              <div className="w-8 h-[1px] bg-primary" />
              <span className="text-[9px] tracking-[0.35em] text-primary uppercase">Панорама 300° · 6 этаж · с 2009 года</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={REVIEW_STATS.yandex.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white/8 border border-white/15 backdrop-blur-sm px-3 py-1.5 hover:border-primary/50 transition-colors"
              >
                <span className="text-[10px] text-yellow-400">★★★★★</span>
                <span className="text-[10px] text-white/80 tracking-wide">5,0 · {formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов</span>
                <span className="text-[9px] text-white/40 tracking-[0.15em] uppercase border-l border-white/15 pl-2">Яндекс</span>
                <span className="hidden sm:inline text-[9px] text-primary/60 tracking-[0.12em] uppercase border-l border-white/15 pl-2">Хорошее место 2026</span>
              </a>
              <a
                href={REVIEW_STATS.twoGis.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white/8 border border-white/15 backdrop-blur-sm px-3 py-1.5 hover:border-primary/50 transition-colors"
              >
                <span className="text-[10px] text-yellow-400">★★★★★</span>
                <span className="text-[10px] text-white/80 tracking-wide">4,9 · {formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов</span>
                <span className="text-[9px] text-white/40 tracking-[0.15em] uppercase border-l border-white/15 pl-2">2ГИС</span>
              </a>
            </div>
          </div>

          <h1 className="font-serif text-[clamp(44px,8vw,120px)] font-light leading-[0.9] tracking-[-0.02em] mb-6 md:mb-8 text-white drop-shadow-lg">
            Ресторан<br />
            <span className="text-accent italic">на крыше</span><br />
            Петербурга
          </h1>

          <p className="text-sm md:text-base leading-[1.8] text-white/70 max-w-[360px] mb-8 md:mb-10">
            Ресторан на крыше в самом центре города. Панорама на&nbsp;300°,
            закаты над Невой, живая музыка — и&nbsp;кухня, которую запоминают.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <button
              className="border border-white text-white px-8 md:px-10 py-3.5 md:py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-white hover:text-background transition-colors"
              onClick={openBookingModal}
            >
              Забронировать стол
            </button>
            <button
              className="border border-primary text-primary px-8 md:px-10 py-3.5 md:py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-white transition-colors"
              onClick={() => setLocation("/interior")}
            >
              Смотреть фото
            </button>
          </div>
        </div>

        {/* Bottom info strip — 2 per row on mobile, all in one row on md+ */}
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10">
          {/* Mobile: 2-column grid */}
          <div className="grid grid-cols-2 md:hidden">
            <div className="px-5 py-4 border-r border-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-0.5">Режим работы</div>
              <div className="text-[12px] text-white/80">{todayHours}</div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-0.5">Адрес</div>
              <MapPicker className="text-[12px] text-white/80" dropUp />
            </div>
            <div className="px-5 py-4 border-r border-white/10 border-t border-t-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-0.5">Сейчас</div>
              <div className={`text-[12px] flex items-center gap-1.5 ${open ? "text-accent" : "text-white/50"}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full pulse-dot shrink-0 ${open ? "bg-accent" : "bg-white/30"}`} />
                {open ? "Открыто" : "Закрыто"}
              </div>
            </div>
            {weather && (
              <div className="px-5 py-4 border-t border-white/10">
                <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-0.5">Погода</div>
                <div className="text-[12px] text-white/80">{weather.temp}°C · {weather.wind} м/с</div>
              </div>
            )}
          </div>

          {/* Desktop: single row */}
          <div className="hidden md:flex">
            <div className="flex-1 px-6 py-5 border-r border-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-1">Высота</div>
              <div className="text-[13px] text-white/80">6-й этаж</div>
            </div>
            <div className="flex-1 px-6 py-5 border-r border-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-1">Режим работы</div>
              <div className="text-[13px] text-white/80">{todayHours}</div>
            </div>
            <div className="flex-1 px-6 py-5 border-r border-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-1">Адрес</div>
              <MapPicker className="text-[13px] text-white/80" dropUp />
            </div>
            <div className="flex-1 px-6 py-5 border-r border-white/10">
              <div className="text-[8px] tracking-[0.25em] text-white/35 uppercase mb-1">Сейчас</div>
              <div className={`text-[13px] flex items-center gap-2 ${open ? "text-accent" : "text-white/50"}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full pulse-dot ${open ? "bg-accent" : "bg-white/30"}`} />
                {open ? "Открыто" : "Закрыто"}
              </div>
            </div>
            <WeatherStrip weather={weather} />
          </div>
        </div>
      </section>

      <NewYearAnnouncement />

      {/* ── VIEW GALLERY ──────────────────────────────────────────────────── */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-6 md:px-14">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-3">Главное</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">Вид, ради которого приезжают</h2>
            </div>
            <button
              className="hidden md:block text-[10px] tracking-[0.2em] uppercase text-accent hover:text-primary transition-colors pb-1 border-b border-accent/30"
              onClick={() => setLocation("/interior")}
            >
              Все фото →
            </button>
          </div>

          {/* 4-photo grid: 1 large left + 3 stacked right */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="md:row-span-2 overflow-hidden aspect-[3/4] md:aspect-auto">
              <img
                src="/photos/view_sunset-sm.webp"
                alt="Закат над Петербургом с крыши"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/interior")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/view_day-sm.webp"
                alt="Дневная панорама Петербурга"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/interior")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/interior-sm.webp"
                alt="Интерьер ресторана"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/interior")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/view_night-sm.webp"
                alt="Ночной Петербург с крыши"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/interior")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/view_summer-sm.webp"
                alt="Летняя терраса"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/interior")}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── USP STRIP (animated counters) ────────────────────────────────── */}
      <section className="bg-card border-y border-primary/15">
        <div className="container mx-auto px-6 md:px-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 divide-primary/15 lg:divide-x">
            <UspCounter value="300°" label="Панорама города" sub="Крыши, купола, Нева" />
            <UspCounter value="6"    label="Этаж над городом" sub="Центр Петербурга" />
            <UspCounter value="15"   label="Лет на рынке" sub="С 2009 года" />
            <UspCounter value="5,0"  label="Рейтинг на Яндексе" sub={`${formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов · Хорошее место`} />
          </div>
        </div>
      </section>

      {/* ── PRESS STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-background border-b border-primary/10 py-10 md:py-12">
        <div className="container mx-auto px-6 md:px-14">
          <div className="flex items-center justify-between mb-7">
            <span className="text-[8px] tracking-[0.4em] text-white/40 uppercase">Пишут о нас</span>
            <a href="/pressa" className="text-[9px] tracking-[0.3em] uppercase text-white/40 hover:text-accent transition-colors">
              Подробнее →
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-6">
            {[
              { name: "Time Out",      sub: "Лучший ресторан с видом" },
              { name: "The Village",   sub: "Гид по Петербургу" },
              { name: "Яндекс",        sub: "Хорошее место 2026" },
              { name: "2ГИС",          sub: "Топ ресторанов СПб" },
              { name: "Afisha Daily",  sub: "Рестораны Петербурга" },
              { name: "РБК Петербург", sub: "Лучшие виды города" },
            ].map((item) => (
              <div key={item.name} className="flex flex-col gap-1.5">
                <span className="font-serif text-xl font-light text-white hover:text-primary transition-colors leading-tight">
                  {item.name}
                </span>
                <span className="text-[8px] tracking-[0.18em] uppercase text-white/45 leading-tight">
                  {item.sub}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOD GALLERY ─────────────────────────────────────────────────── */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-6 md:px-14">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-3">Кухня</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">Авторские блюда с видом на город</h2>
              <p className="mt-3 text-sm text-foreground/55">
                Блюда <span className="text-primary font-medium">от 490 ₽</span>
                <span className="mx-2 text-foreground/25">·</span>
                средний чек <span className="text-primary font-medium">≈ 1300 ₽</span>
              </p>
            </div>
            <button
              className="hidden md:block text-[10px] tracking-[0.2em] uppercase text-accent hover:text-primary transition-colors pb-1 border-b border-accent/30"
              onClick={() => setLocation("/menu")}
            >
              Всё меню →
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="md:row-span-2 overflow-hidden aspect-[3/4] md:aspect-auto">
              <img
                src="/photos/dishes/duck_confit-sm.webp"
                alt="Утиное конфи с тыквенным пюре — авторское горячее ресторана Паруса на крыше"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/menu")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/dishes/pasta_seafood-sm.webp"
                alt="Паста с морепродуктами в чернилах кальмара — фирменное блюдо Паруса"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/menu")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/dishes/scallops_cauliflower-sm.webp"
                alt="Гребешки с цветной капустой и икрой — деликатес ресторана Паруса на крыше"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/menu")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/dishes/pizza_truffle-sm.webp"
                alt="Пицца с чёрным трюфелем из дровяной печи — Паруса на крыше Петербург"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/menu")}
              />
            </div>
            <div className="overflow-hidden aspect-square">
              <img
                src="/photos/dishes/dessert_strawberry-sm.webp"
                alt="Авторский десерт с клубникой и меренгой — Паруса на крыше СПб"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                onClick={() => setLocation("/menu")}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-center md:hidden">
            <button
              className="text-[10px] tracking-[0.2em] uppercase text-accent border border-primary/30 px-8 py-3 hover:bg-primary/10 transition-colors"
              onClick={() => setLocation("/menu")}
            >
              Смотреть всё меню →
            </button>
          </div>
        </div>
      </section>

      {/* ── PHOTO TICKER ─────────────────────────────────────────────────── */}
      <div className="overflow-hidden bg-background py-1 border-b border-primary/10 select-none">
        <div className="ticker-track flex gap-2" style={{ width: "max-content" }}>
          {[...tickerPhotos, ...tickerPhotos].map((photo, i) => (
            <div
              key={i}
              className="shrink-0 overflow-hidden cursor-pointer"
              style={{ width: 280, height: 180 }}
              onClick={() => setLocation("/interior")}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy" decoding="async"
                className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-500 hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── BIRTHDAY / SPECIAL OCCASIONS ──────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <img
          src="/photos/view_clouds.webp"
          alt="Праздник на крыше"
          loading="lazy" decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "rgba(3,13,9,0.78)" }} />
        <div className="relative z-10 py-28 px-8 md:px-16">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-5">Особые поводы</p>
              <h2 className="font-serif text-5xl md:text-6xl font-light text-white leading-[1.05] mb-6">
                День рождения<br /><span className="text-accent italic">над Петербургом</span>
              </h2>
              <p className="text-white/65 leading-[1.9] mb-8 max-w-md">
                Отмечайте праздник там, откуда весь город — как на&nbsp;ладони.
                Панорамный зал, живая музыка, торт и&nbsp;незабываемый вид —
                всё организуем под ключ.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <div className="flex items-center gap-3 bg-primary/20 border border-primary/50 px-5 py-3">
                  <span className="font-['Cormorant_SC'] text-2xl font-bold text-accent leading-none">−20%</span>
                  <span className="text-white/70 text-[11px] leading-tight">на весь счёт<br />за 7 дней до и после дня рождения</span>
                </div>
              </div>
              <button
                className="border border-primary text-primary px-10 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-white transition-colors"
                onClick={() => setLocation("/banket")}
              >
                Узнать подробнее
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { title: "Дни рождения", icon: "✦" },
                { title: "Свадьбы", icon: "✦" },
                { title: "Корпоративы", icon: "✦" },
                { title: "Романтический ужин", icon: "✦" },
              ].map(({ title, icon }) => (
                <div
                  key={title}
                  className="border border-primary/30 bg-background/30 backdrop-blur-sm p-6"
                >
                  <div className="text-primary text-xs mb-3">{icon}</div>
                  <div className="text-white text-sm font-medium">{title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOR TOURISTS ──────────────────────────────────────────────────── */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-6 md:px-14">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 overflow-hidden aspect-[4/3]">
              <img
                src="/photos/food.webp"
                alt="Авторская кухня Паруса на крыше"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-5">Туристам</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground leading-[1.1] mb-6">
                Лучшее место<br />чтобы увидеть<br /><span className="text-accent italic">настоящий Петербург</span>
              </h2>
              <p className="text-muted-foreground leading-[1.9] mb-8">
                Ресторан на крыше в&nbsp;5 минутах от&nbsp;Невского проспекта.
                С&nbsp;открытой террасы виден Исаакиевский собор, крыши
                старого Петербурга и&nbsp;знаменитые закаты белых ночей.
              </p>
              <div className="space-y-3 mb-10">
                {[
                  "Панорамный вид на 300° — Исаакий, Нева, крыши",
                  "Авторское меню с блюдами петербургской кухни",
                  "Живая музыка по пятницам и субботам",
                  "Открытая терраса и застеклённый зал",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="text-primary mt-0.5 shrink-0">—</span>
                    {item}
                  </div>
                ))}
              </div>
              <button
                className="border border-primary text-primary px-10 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-white transition-colors"
                onClick={openBookingModal}
              >
                Забронировать стол
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── EVENTS ────────────────────────────────────────────────────────── */}
      {upcomingEvents && upcomingEvents.length > 0 && (
        <section className="py-24 bg-card border-t border-primary/15">
          <div className="container mx-auto px-6 md:px-14">
            <div className="flex flex-col md:flex-row justify-between items-end mb-14 gap-6">
              <div>
                <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-3">Афиша</p>
                <h2 className="font-serif text-4xl font-light text-foreground">Ближайшие события</h2>
              </div>
              <button
                className="text-[10px] tracking-[0.2em] uppercase text-accent hover:text-primary transition-colors pb-1 border-b border-accent/30"
                onClick={() => setLocation("/afisha")}
              >
                Вся афиша →
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  className="bg-background border border-primary/20 p-8 group cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setLocation("/afisha")}
                >
                  <p className="text-primary text-[10px] font-medium tracking-[0.2em] uppercase mb-5">
                    {formatDate(event.date)}
                  </p>
                  <h3 className="font-serif text-2xl mb-3 font-light leading-tight group-hover:text-primary transition-colors">{event.title}</h3>
                  <p className="text-muted-foreground text-sm font-light leading-relaxed line-clamp-2 mb-6">{event.description}</p>
                  <span className="text-[9px] uppercase tracking-[0.3em] text-accent/60 group-hover:text-primary transition-colors">Подробнее →</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BOOKING ───────────────────────────────────────────────────────── */}
      <section id="booking-section" className="py-24 bg-background border-t border-primary/15">
        <div className="container mx-auto px-6 md:px-14">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-5">Бронирование</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-6">
                Забронируйте<br /><span className="text-accent italic">столик</span>
              </h2>
              <p className="text-muted-foreground leading-[1.9] mb-10">
                Выберите дату, время и количество гостей — бронь мгновенно
                попадает в нашу систему, подтверждение придёт сразу.
              </p>
              <div className="space-y-4 mb-10">
                {[
                  "Подтверждение в течение нескольких минут",
                  "Лучший столик с видом на город",
                  "Можно выбрать повод — день рождения, годовщина",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="text-primary mt-0.5 shrink-0">—</span>
                    {item}
                  </div>
                ))}
              </div>
              <div className="overflow-hidden aspect-[4/3]">
                <img
                  src="/photos/terrace.webp"
                  alt="Терраса ресторана Паруса на крыше"
                  loading="lazy" decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="border border-primary/25 relative overflow-hidden bg-card flex flex-col justify-between" style={{ minHeight: 560 }}>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/40 pointer-events-none z-10" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/40 pointer-events-none z-10" />

              {/* Top decorative block */}
              <div className="px-6 md:px-10 pt-8 md:pt-12 pb-6 border-b border-primary/15">
                <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-4">Онлайн-бронирование</p>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-[1px] bg-primary/40 mt-3 shrink-0" />
                  <p className="font-serif text-2xl font-light text-foreground leading-snug">
                    Стол на сегодня или<br />особый вечер — всё здесь
                  </p>
                </div>
              </div>

              {/* Info rows */}
              <div className="px-6 md:px-10 py-8 space-y-5 flex-1">
                {[
                  { label: "Режим работы", value: `${todayHours} (сегодня)` },
                  { label: "Вместимость", value: "До 300 гостей" },
                  { label: "Телефон", value: "+7 (812) 991-10-90" },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-baseline justify-between gap-4 border-b border-primary/10 pb-4">
                    <span className="text-[9px] tracking-[0.25em] text-primary/60 uppercase">{label}</span>
                    <span className="text-sm text-foreground/80 text-right">{value}</span>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-4 border-b border-primary/10 pb-4">
                  <span className="text-[9px] tracking-[0.25em] text-primary/60 uppercase">Адрес</span>
                  <MapPicker label="Ул. Марата, 5 · 6 этаж" className="text-sm text-foreground/80" dropUp={false} align="right" />
                </div>
              </div>

              {/* CTA */}
              <div className="px-6 md:px-10 pb-8 md:pb-10">
                <button
                  onClick={() => openBookingModal()}
                  className="w-full bg-primary text-background font-['Cormorant_SC'] text-sm tracking-[0.3em] uppercase py-4 hover:bg-accent transition-colors duration-300"
                >
                  Забронировать стол
                </button>
                <p className="text-center text-[10px] text-muted-foreground/50 mt-3 tracking-widest uppercase">
                  Мгновенное подтверждение
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOG ──────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-background border-t border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-3">Гид по Петербургу</p>
              <h2 className="font-serif text-4xl font-light text-foreground">Из нашего блога</h2>
            </div>
            <a
              href="/blog"
              className="hidden md:inline-flex text-[9px] tracking-[0.25em] uppercase text-accent/60 hover:text-accent transition-colors border-b border-accent/20 hover:border-accent/60 pb-0.5"
            >
              Все статьи →
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-primary/10">
            {BLOG_POSTS.slice(0, 3).map((post) => (
              <a
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="bg-background p-8 md:p-10 flex flex-col group hover:bg-card transition-colors"
              >
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">{post.category}</p>
                <h3 className="font-serif text-xl font-light text-foreground leading-snug mb-4 group-hover:text-accent transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-8 line-clamp-3 flex-1">
                  {post.lead}
                </p>
                <div className="flex items-center justify-between border-t border-primary/10 pt-5">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/50">
                    {post.readTime} мин чтения
                  </span>
                  <span className="text-[9px] tracking-[0.2em] uppercase text-accent/50 group-hover:text-accent transition-colors">
                    Читать →
                  </span>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <a
              href="/blog"
              className="text-[9px] tracking-[0.25em] uppercase text-accent/60 hover:text-accent transition-colors border-b border-accent/20 hover:border-accent/60 pb-0.5"
            >
              Все статьи →
            </a>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ───────────────────────────────────────────────────────── */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-card border-t border-primary/15 overflow-hidden">
          <div className="container mx-auto px-6 md:px-14">
            <div className="text-center mb-14">
              <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-3">Впечатления</p>
              <h2 className="font-serif text-4xl font-light text-foreground mb-6">Отзывы гостей</h2>
              <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 mt-6">
              <a
                href={REVIEW_STATS.yandex.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 border border-primary/25 px-7 py-4 hover:border-primary/60 transition-colors group w-full sm:w-auto"
              >
                <div className="text-center shrink-0">
                  <div className="font-serif text-4xl font-light text-primary leading-none mb-1">5,0</div>
                  <div className="flex gap-0.5 justify-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="text-yellow-400 text-sm">★</span>
                    ))}
                  </div>
                </div>
                <div className="border-l border-primary/20 pl-4 text-left flex-1">
                  <div className="text-foreground text-sm font-medium mb-0.5">{formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground">на Яндекс Картах</div>
                  <div className="text-[9px] tracking-[0.15em] uppercase text-primary/70 mt-1">✦ Хорошее место 2026</div>
                </div>
                <span className="text-[9px] tracking-[0.2em] uppercase text-accent/50 group-hover:text-accent transition-colors shrink-0">Читать →</span>
              </a>
              <a
                href={REVIEW_STATS.twoGis.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 border border-primary/25 px-7 py-4 hover:border-primary/60 transition-colors group w-full sm:w-auto"
              >
                <div className="text-center shrink-0">
                  <div className="font-serif text-4xl font-light text-primary leading-none mb-1">4,9</div>
                  <div className="flex gap-0.5 justify-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="text-yellow-400 text-sm">★</span>
                    ))}
                  </div>
                </div>
                <div className="border-l border-primary/20 pl-4 text-left flex-1">
                  <div className="text-foreground text-sm font-medium mb-0.5">{formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground">на 2ГИС</div>
                </div>
                <span className="text-[9px] tracking-[0.2em] uppercase text-accent/50 group-hover:text-accent transition-colors shrink-0">Читать →</span>
              </a>
              </div>
            </div>
            {/* Reviews — CSS scroll-snap on mobile/tablet, CSS grid on desktop */}
            <div className="max-w-5xl mx-auto">
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 md:-mx-14 md:px-14 lg:grid lg:grid-cols-3 lg:overflow-visible lg:mx-0 lg:px-0 lg:pb-0 lg:gap-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {reviews.slice(0, 6).map((review) => {
                  const initials = review.author
                    .split(" ")
                    .map((w: string) => w[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("");
                  return (
                    <div
                      key={review.id}
                      className="snap-start flex-shrink-0 w-[82vw] md:w-[52vw] lg:w-auto relative border border-primary/20 bg-background flex flex-col overflow-hidden transition-all duration-300 hover:border-primary/50 hover:-translate-y-0.5 group"
                    >
                      {/* Hover top accent line */}
                      <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent translate-y-[-1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {/* Decorative background quote */}
                      <div className="absolute top-2 right-4 font-serif text-[9rem] leading-none text-primary/[0.04] select-none pointer-events-none">
                        «
                      </div>
                      <div className="p-8 lg:p-9 flex flex-col flex-1">
                        <p className="font-serif text-[1.05rem] font-light italic text-foreground/90 leading-relaxed flex-1 mb-7">
                          «{review.text}»
                        </p>
                        <div className="border-t border-primary/10 pt-4 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center text-primary text-[11px] font-medium tracking-wider flex-shrink-0">
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <p className="text-primary text-sm font-medium tracking-wide leading-none mb-1 truncate">{review.author}</p>
                              <p className="text-[10px] text-muted-foreground/50">{formatDate(review.date)}</p>
                            </div>
                          </div>
                          <div className="flex gap-px flex-shrink-0">
                            {Array.from({ length: review.rating }).map((_, j) => (
                              <span key={j} className="text-primary text-xs">★</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="text-center mt-12">
              <a
                href="/otzyvy"
                className="inline-flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-accent/60 hover:text-accent transition-colors group"
              >
                Все отзывы гостей
                <span className="w-8 h-[1px] bg-accent/40 group-hover:w-14 group-hover:bg-accent transition-all duration-300" />
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
